import { formatZonedDateTime } from "@sa-nu/shared-utils/date";
import clsx from "clsx";

import type { FragmentType } from "../../../__generated__/gql";
import { graphql, useFragment } from "../../../__generated__/gql";
import type {
  ArchitectureTypeSeries,
  Scalars,
} from "../../../__generated__/gql/graphql";
import { getAccommodationName } from "../../../utils";

export const ReservationSummaryFragment = graphql(`
  fragment ReservationSummary on AccommodationReservation {
    provider {
      __typename
    }
    accommodation {
      name
      code
      site {
        slug
        name
        area {
          slug
        }
      }
      architectureType {
        series
      }
    }
    reservedFrom
    reservedTo
    standardCheckInTime
    detail {
      numberOfAdults
      numberOfKids
      numberOfInfants
      numberOfTotalChildren
      previousLocation
      nextLocation
      allowRoomChange
      internalStamp
    }
    fee {
      billingMethod
      totalAmount
    }
  }
`);

type FormOptions = {
  waiveConsumedSuppliesFee?: boolean;
};

// ReservationSummaryCardが必要とするデータ型（フラグメントに依存しない）
export type ReservationSummaryData = {
  provider: {
    __typename: string;
  };
  accommodation: {
    name: string;
    code?: string | null;
    site?: {
      slug: string;
      name?: string;
      area?: {
        slug: string;
      } | null;
    } | null;
    architectureType?: {
      series: ArchitectureTypeSeries | string;
    } | null;
  };
  reservedFrom: Scalars["ZonedDateTime"]["output"];
  reservedTo: Scalars["ZonedDateTime"]["output"];
  standardCheckInTime: Scalars["ZonedDateTime"]["output"];
  detail: {
    numberOfAdults: number;
    numberOfKids: number;
    numberOfInfants: number;
    numberOfTotalChildren: number;
    previousLocation?: string | null;
    nextLocation?: string | null;
    allowRoomChange?: boolean | null;
    internalStamp?: string | null;
  };
  fee?: {
    billingMethod: string;
    totalAmount: number;
  } | null;
};

export function ReservationSummary({
  reservationData,
  isInternal = false,
  formOptions,
}: {
  reservationData: FragmentType<typeof ReservationSummaryFragment>;
  isInternal?: boolean;
  formOptions?: FormOptions;
}) {
  const r = useFragment(ReservationSummaryFragment, reservationData);
  return (
    <ReservationSummaryCard
      reservation={r}
      isInternal={isInternal}
      formOptions={formOptions}
    />
  );
}

export function ReservationSummaryWithComparison({
  reservationData,
  comparisonData,
  isInternal = false,
  formOptions,
}: {
  reservationData: FragmentType<typeof ReservationSummaryFragment>;
  comparisonData: FragmentType<typeof ReservationSummaryFragment>;
  isInternal?: boolean;
  formOptions?: FormOptions;
}) {
  const r = useFragment(ReservationSummaryFragment, reservationData);
  const c = useFragment(ReservationSummaryFragment, comparisonData);
  return (
    <ReservationSummaryCard
      reservation={r}
      comparison={c}
      isInternal={isInternal}
      formOptions={formOptions}
    />
  );
}

export function ReservationSummaryCard({
  reservation: r,
  comparison: c,
  isInternal = false,
  formOptions,
}: {
  reservation: ReservationSummaryData;
  comparison?: ReservationSummaryData;
  isInternal?: boolean;
  formOptions?: FormOptions;
}) {
  // getAccommodationName は site.name を参照するため存在チェックを行う
  const { site } = r.accommodation;
  const siteName = site?.name;

  const accommodationName =
    site && siteName
      ? getAccommodationName({
          provider: r.provider,
          reservedFrom: r.reservedFrom,
          accommodation: {
            name: r.accommodation.name,
            site: { name: siteName },
          },
        })
      : r.accommodation.name;

  return (
    <div className="border-sbeige border p-[20px]">
      <div
        className={clsx(
          "text-[20px]",
          c && accommodationName !== accommodationName && "text-sorange",
        )}
      >
        {accommodationName}
      </div>
      <div className="font-roboto mt-[10px] space-y-[10px]">
        <div className="text-sm md:text-base">
          <div>
            <span
              className={clsx(
                c &&
                  r.standardCheckInTime !== c.standardCheckInTime &&
                  "text-sorange",
              )}
            >
              {formatZonedDateTime(r.standardCheckInTime, {
                withDayOfWeek: true,
              })}
            </span>
            <span className="ml-[10px] text-[11px]">チェックイン</span>
          </div>
          <div>
            <span
              className={clsx(
                c && r.reservedTo !== c.reservedTo && "text-sorange",
              )}
            >
              {formatZonedDateTime(r.reservedTo, {
                withDayOfWeek: true,
              })}
            </span>
            <span className="ml-[10px] text-[11px]">チェックアウト</span>
          </div>
        </div>
        <div className="text-[12px]">
          <span
            className={clsx(
              c &&
                r.detail.numberOfAdults !== c.detail.numberOfAdults &&
                "text-sorange",
            )}
          >
            大人{r.detail.numberOfAdults}名
          </span>
          <span
            className={clsx(
              "ml-[4px]",
              c &&
                r.detail.numberOfTotalChildren !==
                  c.detail.numberOfTotalChildren &&
                "text-sorange",
            )}
          >
            {r.detail.numberOfKids + r.detail.numberOfInfants > 0 &&
              `子供${r.detail.numberOfKids + r.detail.numberOfInfants}名`}
          </span>
        </div>
        <div className="text-[12px]">
          <span
            className={clsx(
              c &&
                r.detail.previousLocation !== c.detail.previousLocation &&
                "text-sorange",
            )}
          >
            前泊地 {r.detail.previousLocation}
          </span>
          <span
            className={clsx(
              "ml-[12px]",
              c &&
                r.detail.nextLocation !== c.detail.nextLocation &&
                "text-sorange",
            )}
          >
            行先地 {r.detail.nextLocation}
          </span>
        </div>
        {/* 料金合計と部屋変更OK/NGは全ユーザーに表示 */}
        {r.fee && (
          <div className="mt-[10px] text-[12px]">
            <span
              className={clsx(
                c &&
                  c.fee &&
                  r.fee.totalAmount !== c.fee.totalAmount &&
                  "text-sorange",
              )}
            >
              料金合計 ¥{r.fee.totalAmount.toLocaleString()}
            </span>
          </div>
        )}
        <div className="mt-[8px] flex flex-wrap gap-[8px] text-[11px]">
          <span
            className={clsx(
              "rounded-[4px] px-[6px] py-[2px]",
              r.detail.allowRoomChange
                ? "bg-green-100 text-green-800"
                : "bg-red-100 text-red-800",
            )}
          >
            部屋変更{r.detail.allowRoomChange ? "OK" : "NG"}
          </span>
          {/* 以下はAdmin専用表示 */}
          {isInternal && (
            <>
              {r.detail.internalStamp === "🌟" && (
                <span className="rounded-[4px] bg-purple-100 px-[6px] py-[2px] text-purple-800">
                  重要宿泊 🌟
                </span>
              )}
              {formOptions?.waiveConsumedSuppliesFee && (
                <span className="rounded-[4px] bg-orange-100 px-[6px] py-[2px] text-orange-800">
                  有料備品 無料
                </span>
              )}
              {r.fee?.billingMethod === "Manual" && (
                <span className="rounded-[4px] bg-blue-100 px-[6px] py-[2px] text-blue-800">
                  別途請求
                </span>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
